import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { TYRE_POSITIONS } from "models";
import "modules/CarDetails/components/TruckTyres/TruckTyres.scss";
import { useCarLocation } from "modules/CarDetails/hooks";
import { getTyreSeasonIcon } from "utils/common";

import type { TFunction } from "i18next";
import type { DealerLocation, TruckTyres as TruckTyreModel } from "models";
import type { CarWithGroupedTruckTyres } from "modules/CarDetails/hooks";

type TruckTyresProps = {
  car: CarWithGroupedTruckTyres;
};

const PositionMap = new Map<TYRE_POSITIONS, string>([
  [TYRE_POSITIONS.TruckLeft, "L"],
  [TYRE_POSITIONS.TruckRight, "R"],
  [TYRE_POSITIONS.TruckExteriorLeft, "EL"],
  [TYRE_POSITIONS.TruckExteriorRight, "ER"],
  [TYRE_POSITIONS.TruckInteriorLeft, "IL"],
  [TYRE_POSITIONS.TruckInteriorRight, "IR"],
  [TYRE_POSITIONS.TruckSpare, "S"]
]);

const getTyreHeading = (spareIndex: number, index: number, t: TFunction) =>
  index >= spareIndex ? `${t("v8_spare_tyre", "Spare Tyre")} ${index - spareIndex + 1}` : `${t("v8_axle", "Axle")} ${index + 1}`;

const checkLayoutPosition = (position: number) =>
  position === TYRE_POSITIONS.TruckRight || position === TYRE_POSITIONS.TruckExteriorRight || position === TYRE_POSITIONS.TruckInteriorLeft;

const getTyreColour = (profile: number, carLocation?: DealerLocation) => {
  if (profile < (carLocation?.truck_tyre_n_threshold ?? 0)) return "red";
  if (profile < (carLocation?.truck_tyre_a_threshold ?? 0)) return "orange";
  return "green";
};

const renderTruckTyre = (truckTyre: TruckTyreModel, carLocation?: DealerLocation) => {
  const { tyre, position, axle, profile } = truckTyre;
  if (!tyre) return null;

  const tyreSeasonIcon = getTyreSeasonIcon(tyre.season);
  const isRightLayout = checkLayoutPosition(position);

  const { manufacturer, description, width, height, size, speed, carry_capacity } = tyre;
  const fullDescription = !description.includes("R") && !description.includes("/") ? `${description} ${width}/${height}R${size} ${carry_capacity}${speed}` : description;

  return (
    <div key={`${axle}-${position}`}>
      <div className={`flex-row small-gap tyre-details-font ${isRightLayout ? "end row-reverse" : "start"}`}>
        <div className={`tyre-indicator ${getTyreColour(profile, carLocation)}`}>
          <p>{PositionMap.get(position) ?? ""}</p>
        </div>
        <p>{`${profile} mm`}</p>
      </div>
      <div className={`tyre-details tyre-details-font ${isRightLayout ? "right" : ""}`}>
        <p>
          {tyreSeasonIcon && isRightLayout && (
            <span>
              <Icon className={tyreSeasonIcon} />
            </span>
          )}
          {manufacturer}
          {tyreSeasonIcon && !isRightLayout && (
            <span>
              <Icon className={tyreSeasonIcon} />
            </span>
          )}
        </p>
        <p>{fullDescription}</p>
      </div>
    </div>
  );
};

export const hasTruckTyres = (car?: CarWithGroupedTruckTyres) => !!car?._groupedTruckTyres.length;

export const TruckTyres = ({ car }: TruckTyresProps) => {
  const { t } = useTranslation();

  const carLocation = useCarLocation();

  if (!hasTruckTyres(car)) return null;

  return (
    <div className="Tyres">
      <h4>{car.is_truck ? t("v8_on_truck", "On Truck") : t("v8_on_vehicle", "On Vehicle")}</h4>
      {car._groupedTruckTyres.map((tyreGroup, index) => {
        return (
          <div className="flex-row" key={"tyre-group-" + index}>
            <div className="tyre-container">
              <div className="header">
                <h4>{getTyreHeading(car._spareIndex, index, t)}</h4>
              </div>
              <div className="flex-row padding">{tyreGroup.map(tyre => renderTruckTyre(tyre, carLocation))}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
